<template lang="pug">
el-alert(
  v-if="templateNotMatching",
  type="error",
  :title="$t('systemMisconfigured')",
  :closable="false"
)
el-alert(
  v-else-if="error",
  type="error",
  :title="getResponseErrorMessage(errorObject, $t)",
  :closable="false"
)
.twin-management-detail(v-else-if="!isNil(asset)", v-loading="loading")
  SmxTitleActions(:title="title")
    template(#actions)
      slot(v-if="isEditing && mq.mdPlus", name="actions")
      //- NOTE: we v-if the tab since it might need to reload after changes in other tabs
      template(v-if="currentTab === 'details'")
        el-button(
          v-if="editModeAvailable && !editMode && mq.mdPlus",
          type="primary",
          icon="Edit",
          @click="switchEditMode"
        ) {{ $t("edit") }}
        el-button(
          v-else-if="editModeAvailable && !editMode",
          type="primary",
          icon="Edit",
          circle,
          @click="switchEditMode"
        )
  .flex.flex-col.twin-actions.mb-8
    slot(v-if="isEditing && !mq.mdPlus", name="actions")
  .subtitle-container
    slot(name="subtitle", :twin="asset")
  el-tabs(
    ref="tabsRef",
    v-if="hasExistingTwin && resolvedTwinId !== undefined",
    v-model="currentTab"
  )
    template(v-if="!isReadOnlyPreview")
      el-tab-pane(
        v-for="tab in settings.customTabs?.filter((t) => t.beforeOtherTabs && (!t.condition || t.condition(asset)))",
        :key="tab.name",
        :label="$t(tab.label)",
        :name="tab.name"
      )
        component(
          v-if="currentTab === tab.name",
          :is="tab.component",
          :twin="asset"
        )

    el-tab-pane(:label="$t('details')", name="details")
      .flex.flex-col
        slot(name="beforeDetails", :twin="asset")
        .flex.flex-row.flex-wrap.justify-between
          TwinDataViewEdit(
            :twin="isReadOnlyPreview ? asset : undefined",
            :editMode="editMode",
            :isEditing="isEditing",
            :commentVisible="commentVisible",
            :hideBatchCreate="props.settings.hideBatchCreate",
            :commentRequired="commentRequired",
            :commentRequiredOnEdit="props.commentRequiredOnEdit",
            :hide-qr-input="props.settings.hideQrInput",
            :settings="props.settings",
            @endEdit="unsetEditMode",
            @created="onCreated",
            @multipleCreated="onMultipleCreated",
            @cancel="onCancel",
            @deleteTwin="onDeleteTwin"
          )
            template(
              v-for="prop in asset.properties?.filter((p) => hasSlot(p.customId))",
              :key="`psl-${prop.uuid}`",
              v-slot:[prop.customId]="slotProps"
            )
              slot(:name="prop.customId", v-bind="slotProps")

          .flex.flex-col.items-end.mb-8(
            v-if="isEditing",
            class="max-md:w-full max-md:items-center"
          )
            slot(name="metadata")
            .max-w-64.mb-8(v-if="!props.settings.hideQrCode")
              TwinQrCode(:twin="asset")
            TwinImages(
              v-if="!editMode",
              :twin-id="resolvedTwinId",
              :disabled="isReadOnlyPreview"
            )

    el-tab-pane(:label="$t('documents')", name="documents", lazy)
      template(
        v-for="documentType in (settings.documents ?? [{ title: 'documents', category: undefined }])",
        :key="documentType.title"
      )
        h3.mb-4 {{ $t(documentType.title) }}
        TwinDocumentsTable(
          :twin-id="resolvedTwinId",
          :category-links="settings.documents?.filter((d) => d.category !== documentType.category)",
          :category="documentType.category",
          :read-only="isReadOnlyPreview",
          @open-upload-dialog="openDocumentUploadDialog"
        )

    template(v-if="settings.referenceTabs")
      el-tab-pane(
        v-for="refTab in settings.referenceTabs",
        :key="refTab.label",
        :label="$t(refTab.label)",
        :name="refTab.label",
        lazy
      )
        TwinReferences(
          :twin="asset",
          :hide-reference-types="settings.hideReferenceTypes",
          :include-reference-types="refTab.includeReferenceTypes"
        )
    el-tab-pane(v-else, :label="$t('references')", name="references", lazy)
      TwinReferences(
        :twin="asset",
        :hide-reference-types="settings.hideReferenceTypes"
      )

    el-tab-pane(
      v-if="settings.tickets && settings.tickets.length > 0",
      :label="$t('tickets')",
      name="tickets",
      lazy
    )
      template(
        v-for="(ticketConfig, index) in settings.tickets",
        :key="ticketConfig.type"
      )
        .flex.justify-between.items-center.mb-4(:class="{ 'mt-8': index > 0 }")
          h3 {{ $t(ticketConfig.label) }}
          el-button(
            v-if="ticketConfig.createComponent && !isReadOnlyPreview",
            type="primary",
            icon="Plus",
            @click="openCreateTicketDialog(index)"
          ) {{ $t("createTicket") }}
        TwinTableDataLoading(
          :id="`${resolvedTwinId}-tickets-${ticketConfig.type}`",
          :query="ticketsQueryForType(ticketConfig.type)",
          :template-query="{ type: ticketConfig.type }",
          :hide-delete="true",
          :property-ids="ticketVisibleProperties",
          :show-created="true",
          :instant-search="ticketRefreshKey",
          @row-click="onTicketRowClick"
        )
          template(#name="scope")
            router-link.link(
              :to="{ name: appStore.getTwinEditLink(scope.data.twin).routeName, params: { id: scope.data.twin.uuid }, query: { last: 'myTickets' } }"
            ) {{ scope.value }}
          template(#priority="scope")
            PriorityTag(:priority="scope.value")
          template(#status="scope")
            StatusTag(:status="scope.value")

    el-tab-pane(:label="$t('changelog')", name="changelog", lazy)
      TwinChangelog(:twin-id="resolvedTwinId")

    template(v-if="!isReadOnlyPreview")
      el-tab-pane(
        v-for="tab in settings.customTabs?.filter((t) => !t.beforeOtherTabs && (!t.condition || t.condition(asset)))",
        :key="tab.name",
        :label="$t(tab.label)",
        :name="tab.name"
      )
        //- NOTE: we v-if the tab since it might need to reload after changes in other tabs
        component(
          v-if="currentTab === tab.name",
          :is="tab.component",
          :twin="asset"
        )

  template(v-else)
    .flex.flex-col
      slot(name="beforeDetails", :twin="asset")
      .flex.flex-row.flex-wrap.justify-between
        TwinDataViewEdit(
          :twin="isReadOnlyPreview ? asset : undefined",
          :editMode="editMode",
          :isEditing="isEditing",
          :commentVisible="commentVisible",
          :hideBatchCreate="props.settings.hideBatchCreate",
          :commentRequired="commentRequired",
          :commentRequiredOnEdit="props.commentRequiredOnEdit",
          :hide-qr-input="props.settings.hideQrInput",
          :settings="props.settings",
          @endEdit="unsetEditMode",
          @created="onCreated",
          @multipleCreated="onMultipleCreated",
          @cancel="onCancel",
          @deleteTwin="onDeleteTwin"
        )
          template(
            v-for="prop in asset.properties?.filter((p) => hasSlot(p.customId))",
            :key="`psl-${prop.uuid}`",
            v-slot:[prop.customId]="slotProps"
          )
            slot(:name="prop.customId", v-bind="slotProps")

        .flex.flex-col.items-end.mb-8(
          v-if="isEditing",
          class="max-md:w-full max-md:items-center"
        )
          slot(name="metadata")
          .max-w-64.mb-8(v-if="!props.settings.hideQrCode")
            TwinQrCode(:twin="asset")
          TwinImages(v-if="!editMode", :twin-id="resolvedTwinId")

  TwinDocumentUploadDialog(
    v-if="!isReadOnlyPreview && props.id",
    v-model:visible="documentUploadDialogVisible",
    :twin-id="props.id",
    :category="documentUploadCategory",
    :document-types="settings.documents ?? []",
    @uploaded="onDocumentsUploaded"
  )

  el-dialog(
    v-if="!isReadOnlyPreview",
    v-model="createTicketDialogVisible",
    :title="$t('createTicket')"
  )
    component(
      v-if="activeCreateComponent",
      :is="activeCreateComponent",
      :asset-id="resolvedTwinId",
      @created="onTicketCreated"
    )
</template>

<script setup lang="ts">
/**
 * NOTE: this is used to either edit or create an asset.
 * If the id is the id of a template, that means we want to create a new asset,
 * if it is the id of a non-template asset, it means we want to edit the existing asset
 */
import {
  computed,
  onMounted,
  ref,
  onUnmounted,
  watch,
  useSlots,
  type Component,
} from "vue";
import { useTwinStore } from "../../stores/twinManagement";
import { useTwinDocumentsStore } from "../../stores/documents";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import TwinChangelog from "./components/TwinChangelog.vue";
import TwinDocumentUploadDialog from "./components/TwinDocumentUploadDialog.vue";
import TwinDocumentsTable from "@shared/components/elements/TwinDocumentsTable.vue";
import { useMq } from "vue3-mq";
import TwinReferences from "./components/TwinReferences.vue";
import TwinTableDataLoading from "./components/TwinTableDataLoading.vue";
import { useTwinEditCreateStore } from "../../stores/twinCreateEdit";
import { type TwinManagementSettings } from "./types";
import { useAppStore } from "../../stores/apps";
import {
  getResponseErrorMessage,
  showResponseErrorToUser,
} from "@shared/util/genericResponseErrors";
import { ElNotification } from "element-plus";
import {
  type TwinResponse,
  type TwinQuery,
} from "@shared/services/twinManagement";
import { type AxiosResponse } from "axios";
import TwinDataViewEdit from "./components/TwinDataViewEdit.vue";
import TwinQrCode from "./components/TwinQrCode.vue";
import TwinImages from "../elements/TwinImages.vue";
import { isNil } from "lodash";
import PriorityTag from "../tickets/components/PriorityTag.vue";
import StatusTag from "../tickets/components/StatusTag.vue";
import SmxTitleActions from "../SmxTitleActions.vue";

import { usePersistantTabs } from "@shared/hooks/tabPersistance";

const props = defineProps<{
  template?: string;
  id?: string;
  preview?: boolean;
  twin?: TwinResponse;
  settings: TwinManagementSettings;
  showCommentOnCreate?: boolean;
  commentRequiredOnEdit?: boolean;
  expandReferences?: boolean;
  hideDocuments?: boolean;
}>();

const { t } = useI18n();

const appStore = useAppStore();

const mq = useMq();

const twinEditCreateStore = useTwinEditCreateStore();
const inputs = computed(() => twinEditCreateStore.inputs);

const editMode = ref(false);

const twinStore = useTwinStore();
const documentsStore = useTwinDocumentsStore();

const isReadOnlyPreview = computed(() => props.preview === true);
const previewTwinLoadingState = computed(() =>
  isReadOnlyPreview.value && !props.twin && props.id
    ? twinStore.getTwinLoadingState(props.id)
    : undefined,
);
const previewTwin = computed(
  () => props.twin ?? previewTwinLoadingState.value?.result,
);
const resolvedTwinId = computed(() =>
  isReadOnlyPreview.value
    ? (previewTwin.value?.uuid ?? props.id)
    : props.id,
);
const resolvedTemplate = computed(() =>
  isReadOnlyPreview.value
    ? (previewTwin.value?.templateUuid ?? props.template)
    : props.template,
);
const asset = computed(() =>
  isReadOnlyPreview.value ? previewTwin.value : twinEditCreateStore.asset,
);
const isEditing = computed(() =>
  isReadOnlyPreview.value ? true : twinEditCreateStore.isEditing,
);
const hasExistingTwin = computed(() =>
  isReadOnlyPreview.value ? previewTwin.value !== undefined : isEditing.value,
);

// Preview callers pass the already-loaded twin to avoid a duplicate request.
const templateNotMatching = computed(() => {
  if (isReadOnlyPreview.value) {
    if (!previewTwin.value && !props.id) {
      return true;
    }
    return (
      previewTwin.value !== undefined &&
      props.template !== undefined &&
      previewTwin.value.templateUuid !== props.template
    );
  }
  if (twinEditCreateStore.asset?.templateUuid) {
    return twinEditCreateStore.asset?.templateUuid !== resolvedTemplate.value;
  }
  return false;
});

const error = computed(() =>
  isReadOnlyPreview.value
    ? previewTwinLoadingState.value?.error
    : twinStore.twinLoadingState.error,
);
const errorObject = computed(() =>
  isReadOnlyPreview.value
    ? previewTwinLoadingState.value?.errorObject
    : twinStore.twinLoadingState.errorObject,
);
const loading = computed(() =>
  isReadOnlyPreview.value
    ? previewTwinLoadingState.value?.loading
    : twinStore.twinLoadingState.loading,
);

const title = computed(() => {
  if (isReadOnlyPreview.value) {
    return asset.value?.name;
  }
  if (isEditing.value) {
    return asset.value?.name;
  } else {
    return t("createName", {
      name: asset.value?.name || t("newEntry").toLocaleLowerCase(),
    });
  }
});

const editModeAvailable = computed(() => {
  return (
    !isReadOnlyPreview.value &&
    isEditing.value &&
    asset.value &&
    asset.value.writeAccess
  );
});

function setEditMode() {
  if (isReadOnlyPreview.value) {
    return;
  }
  // TODO
  twinEditCreateStore.resetData();
  void router.replace({
    name: props.settings.detailViewName,
    params: { id: props.id, template: props.template },
    query: { edit: "true" },
  });
  editMode.value = true;
}

function unsetEditMode() {
  if (isReadOnlyPreview.value) {
    return;
  }
  void router.replace({
    name: props.settings.detailViewName,
    params: { id: props.id, template: props.template },
    query: { edit: "false" },
  });
  editMode.value = false;
}

function switchEditMode() {
  if (editMode.value) {
    unsetEditMode();
  } else {
    setEditMode();
  }
}

const route = useRoute();

function initData() {
  if (isReadOnlyPreview.value) {
    return;
  }
  // NOTE: unfortunately vue router will not update props of param updates, thus we have to use the router directly in order to support links to the same page
  let id = router.currentRoute.value.params.id as string | undefined;
  let templateId = router.currentRoute.value.params.template as
    | string
    | undefined;

  if (id === undefined) {
    id = props.id;
  }

  if (templateId === undefined) {
    templateId = props.template;
  }

  twinEditCreateStore.init(templateId, id).catch((e) => {
    showResponseErrorToUser(e, t);
  });
  editMode.value = router.currentRoute.value.query.edit === "true";
}

onMounted(() => {
  if (isReadOnlyPreview.value) {
    if (!props.twin && props.id) {
      void twinStore.loadTwinCached(props.id, false).catch((e) => {
        showResponseErrorToUser(e, t);
      });
    }
    return;
  }
  // fixes a safari bug
  window.scrollTo(0, 0);
  initData();
});

onUnmounted(() => {
  if (!isReadOnlyPreview.value) {
    twinEditCreateStore.endEditing();
  }
});

// Comment
const commentRequired = computed(
  () => isEditing.value && props.commentRequiredOnEdit,
);
const commentVisible = computed(
  () =>
    (!isEditing.value && props.showCommentOnCreate) ||
    (isEditing.value && editMode.value && props.commentRequiredOnEdit),
);

const router = useRouter();

// watch(router.currentRoute, initData);

// Asset references

const defaultTab = props.settings.defaultTab || "details";
const { currentTab, tabsRef } = isReadOnlyPreview.value
  ? { currentTab: ref(defaultTab), tabsRef: ref() }
  : usePersistantTabs(defaultTab);

const documentUploadDialogVisible = ref(false);
const documentUploadCategory = ref("twinDocument");

function openDocumentUploadDialog(category: string) {
  if (isReadOnlyPreview.value) {
    return;
  }
  documentUploadCategory.value = category;
  documentUploadDialogVisible.value = true;
}

function onDocumentsUploaded() {
  if (isReadOnlyPreview.value || !props.id) {
    return;
  }
  const twinId = props.id;
  const categories = props.settings.documents
    ? props.settings.documents.map((documentType) => documentType.category)
    : ["twinDocument"];
  categories.forEach((category) => {
    documentsStore.reloadTwinDocumentsByCategory(
      twinId,
      category ?? "twinDocument",
    );
  });
}

const createTicketDialogVisible = ref(false);
const activeCreateComponent = ref<Component | undefined>(undefined);
const ticketRefreshKey = ref("");
const ticketVisibleProperties = [
  "status",
  "dueDate",
  "startDate",
  "priority",
  "assignee",
];

function ticketsQueryForType(type: string): TwinQuery | undefined {
  if (!resolvedTwinId.value) {
    return undefined;
  }
  return {
    type,
    isTemplate: false,
    showProperties: true,
    active: true,
    properties: [
      {
        customId: "asset",
        type: "twinRelation",
        values: [resolvedTwinId.value],
      },
    ],
  };
}

function openCreateTicketDialog(index: number) {
  if (isReadOnlyPreview.value) {
    return;
  }
  activeCreateComponent.value =
    props.settings.tickets?.[index]?.createComponent;
  createTicketDialogVisible.value = true;
}

function onTicketCreated() {
  createTicketDialogVisible.value = false;
  ticketRefreshKey.value = Date.now().toString();
}

function onTicketRowClick(ticket: TwinResponse) {
  const route = appStore.getTwinEditLink(ticket);
  void router.push({
    name: route?.routeName,
    params: { id: ticket.uuid, template: ticket.templateUuid },
  });
}

// Init data from query parameters

watch(asset, initDataFromQueryParams);

onMounted(initDataFromQueryParams);

function initDataFromQueryParams() {
  if (isReadOnlyPreview.value || !asset.value) {
    return;
  }
  const propRegex = new RegExp("prop.*");
  const data = Object.keys(route.query)
    .filter((key) => propRegex.test(key))
    .forEach((key) => {
      // Find the properties and set their value
      const customId = key.split(".")[1];
      const prop = asset.value!.properties!.find(
        (item) => item.customId === customId,
      );
      if (prop !== undefined) {
        // TODO: this is incomplete, the type should be properly checked and converted
        // Currently we only support a single relation
        if (prop.type === "twinRelation") {
          inputs.value[prop.uuid] = [route.query[key]];
        } else {
          inputs.value[prop.uuid] = route.query[key];
        }
      }
    });
}

function onCreated(response: AxiosResponse<TwinResponse>) {
  if (props.settings.routeAfterSave) {
    void router.push(props.settings.routeAfterSave);
  } else {
    void router.push({
      name: props.settings.detailViewName,
      params: { template: props.template, id: response.data?.uuid },
    });
  }
  // Edit more, disabled for now
  // else {
  //   // Reset the values
  //   twinEditCreateStore.resetData();
  //   name.value = "";
  //   description.value = "";
  //   initData();
  //   initDataFromQueryParams();
  // }
  ElNotification({
    title: t("success"),
    message: t("entryAdded"),
    type: "success",
  });
}

function onMultipleCreated() {
  void router.push({
    name: props.settings.listViewName,
    params: { template: props.template },
  });
}

function onCancel() {
  if (isEditing.value) {
    twinEditCreateStore.resetData();
    unsetEditMode();
  } else {
    router.back();
  }
}

async function onDeleteTwin() {
  if (props.id) {
    await twinStore.deleteTwin(props.id);
    void router.push({
      name: props.settings.listViewName,
      params: { template: props.template },
    });
  }
}

const slots = useSlots();

function hasSlot(name?: string) {
  if (name === undefined) {
    return false;
  }
  return !!slots[name];
}
</script>

<style scoped>
.twin-actions > :deep(.el-button) {
  margin-top: 1rem;
  margin-left: 0;
}
</style>
